import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { useTranslate } from 'react-admin';

interface MediaViewerProps {
  open: boolean;
  onClose: () => void;
  url: string;
  fileName?: string | null;
  fileType: 'image' | 'video' | 'pdf' | 'document';
  caption?: string | null;
}

/**
 * Fetch a file as a blob and return an object URL for inline display.
 * This bypasses S3 Content-Disposition: attachment headers that prevent
 * PDFs from rendering in iframes.
 */
function useBlobUrl(srcUrl: string, enabled: boolean) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const prevUrl = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !srcUrl) {
      setBlobUrl(null);
      return;
    }

    // Avoid re-fetching for the same URL
    if (srcUrl === prevUrl.current && blobUrl) return;
    prevUrl.current = srcUrl;

    let revoked = false;
    setLoading(true);
    setError(false);

    fetch(srcUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        const objectUrl = URL.createObjectURL(
          new Blob([blob], { type: 'application/pdf' }),
        );
        setBlobUrl(objectUrl);
        setLoading(false);
      })
      .catch(() => {
        if (revoked) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      revoked = true;
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [srcUrl, enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { blobUrl, loading, error };
}

export function MediaViewer({
  open,
  onClose,
  url,
  fileName,
  fileType,
  caption,
}: MediaViewerProps) {
  const translate = useTranslate();
  const [zoom, setZoom] = useState(1);
  const [loading, setLoading] = useState(true);

  // For PDFs: fetch as blob to bypass S3 Content-Disposition: attachment
  const {
    blobUrl: pdfBlobUrl,
    loading: pdfBlobLoading,
    error: pdfBlobError,
  } = useBlobUrl(url, open && fileType === 'pdf');

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5));
  const handleReset = () => setZoom(1);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Top toolbar */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'rgba(0, 0, 0, 0.8)',
        }}
      >
        <Box sx={{ color: 'white', fontSize: '1rem', maxWidth: '60%' }}>
          {fileName || translate('resources.chat.media')}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {fileType === 'image' && (
            <>
              <IconButton onClick={handleZoomOut} sx={{ color: 'white' }}>
                <ZoomOutIcon />
              </IconButton>
              <IconButton onClick={handleReset} sx={{ color: 'white' }}>
                <Box sx={{ fontSize: '0.875rem' }}>{Math.round(zoom * 100)}%</Box>
              </IconButton>
              <IconButton onClick={handleZoomIn} sx={{ color: 'white' }}>
                <ZoomInIcon />
              </IconButton>
            </>
          )}
          <IconButton onClick={handleDownload} sx={{ color: 'white' }}>
            <DownloadIcon />
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0,
          mt: 8,
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          >
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        )}

        {fileType === 'image' && (
          <Box
            component="img"
            src={url}
            alt={fileName || 'Image'}
            onLoad={() => setLoading(false)}
            sx={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `scale(${zoom})`,
              transition: 'transform 0.2s ease-in-out',
              cursor: zoom > 1 ? 'move' : 'default',
            }}
          />
        )}

        {fileType === 'video' && (
          <Box
            component="video"
            src={url}
            controls
            autoPlay
            onLoadedData={() => setLoading(false)}
            sx={{
              maxWidth: '90%',
              maxHeight: '80vh',
              objectFit: 'contain',
            }}
          />
        )}

        {fileType === 'pdf' && (
          <>
            {pdfBlobLoading && !pdfBlobUrl && (
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress sx={{ color: 'white' }} />
              </Box>
            )}
            {pdfBlobError && (
              <Box sx={{ color: 'white', textAlign: 'center', p: 4 }}>
                <Box sx={{ mb: 2 }}>{translate('resources.chat.fileCannotBePreview')}</Box>
                <IconButton
                  onClick={handleDownload}
                  sx={{ color: 'white', border: '1px solid white', borderRadius: 1, px: 3, py: 1 }}
                >
                  <DownloadIcon sx={{ mr: 1 }} />
                  {translate('resources.chat.downloadFile')}
                </IconButton>
              </Box>
            )}
            {pdfBlobUrl && (
              <Box
                component="iframe"
                src={`${pdfBlobUrl}#toolbar=1`}
                onLoad={() => setLoading(false)}
                sx={{
                  width: '90%',
                  height: '85vh',
                  border: 'none',
                  bgcolor: 'white',
                }}
              />
            )}
          </>
        )}

        {fileType === 'document' && (
          <Box
            sx={{
              color: 'white',
              textAlign: 'center',
              p: 4,
            }}
          >
            <Box sx={{ mb: 2 }}>{translate('resources.chat.fileCannotBePreview')}</Box>
            <IconButton
              onClick={handleDownload}
              sx={{
                color: 'white',
                border: '1px solid white',
                borderRadius: 1,
                px: 3,
                py: 1,
              }}
            >
              <DownloadIcon sx={{ mr: 1 }} />
              {translate('resources.chat.downloadFile')}
            </IconButton>
          </Box>
        )}

        {caption && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              p: 2,
              textAlign: 'center',
            }}
          >
            {caption}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
